/**
 * <ZoomPanel>
 *
 * Sticky right-rail panel that hosts the live Zoom session UI for the AI
 * Scribe (Phase 2 item 2.7 of the AI Scribe rollout). The 8-section layout
 * is documented in `App_BeHuman/docs/ui-designs/06-telehealth-and-scribe.md`;
 * the reference implementation is at
 * `ui-designs/src/apps/provider-portal/pages/ChartPage.tsx:5121-5597`.
 *
 * Sections (top → bottom):
 *   §1  ZoomPanelHeader        — LIVE dot + timer + Scribe + End
 *   §2  PatientVideoFrame      — 4:3 gradient + avatar + PiP self-view
 *   §3  Session info           — patient name + provider/visit
 *   §4  Controls row           — PanelBtns (Mute / Video / Background* / Chat / AI)
 *                                  *Background only for VB-eligible doctors
 *   §6  VisitGuidanceSection   — Risks & Gaps visit topics as dismissible cards
 *   §9  AskAssistantSection    — patient-pinned BeAssistant chat (starts collapsed)
 *   §5  AIScribeSection        — state-aware header + idle prompt OR …
 *        (§6/§9/§5 split the column via draggable vertical dividers between
 *        open sections; collapsed sections keep their intrinsic height)
 *   §7  TranscriptStream(dark) — diarized PROVIDER / PATIENT bubbles (starts collapsed)
 *   §8  EndSessionPrompt       — internal absolute modal (when triggered)
 *
 * Hidden when `useCallSession().isJoined === false`. Renders nothing —
 * EXCEPT in page mode when `showJoinPrompt` or `connecting` is set, where it
 * renders the compact `<ZoomPanelPreJoin>` slot — the explicit "Join Session"
 * CTA, or its spinner variant while a join is in flight — so a doctor on
 * `/call/:encounterId` always sees the rail rather than a blank gap.
 *
 * Width
 *   Default 380 px, drag-resizable 380–700 px (in-memory only).
 *   `parentMode` is kept on the props for API compatibility but no longer
 *   drives the default width — the new spec is parent-agnostic.
 *
 * Plumbing
 *   - `useCallSession()` — current encounter + scribe state.
 *   - `useScribeSession()` — start / pause / resume of the scribe lifecycle.
 *   - `useLiveTranscription()` — captions, `isStreaming`, and `flushPending` (awaited by finalize).
 *   - `useFinalizeScribe()` — Create-Progress-Note / End-Without-Note flow.
 *   - `useScribeFormBridge()` — pushes AI draft into the SOAP form and asks
 *     the chrome to surface the SOAP pane.
 *
 * Phase 1 (rip-out) — removed the identity-verification gate (LD-4) and the
 * AI-suggested-screenings modal (LD-5).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  IconArrowsMinimize,
  IconBackground,
  IconVideo,
  IconVideoOff,
  IconLink,
  IconMessageCircle,
  IconMicrophone,
  IconMicrophoneOff,
  IconScript,
} from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { showNotification } from '@mantine/notifications'
// `react-resizable-panels` v4 renamed `PanelGroup` → `Group` and
// `PanelResizeHandle` → `Separator`. Direction is now `orientation`.
// Sizing accepts strings (`"60%"`) or numbers (percent). No `autoSaveId`
// shortcut anymore — persistence is wired via `defaultLayout` /
// `onLayoutChange` + a `LayoutStorage` slot when we need it.
import { Group as ResizableGroup, Panel, Separator } from 'react-resizable-panels'
import { useCallSession } from '../session'
import { useScribeSession } from '@/pages/Call/hooks/useScribeSession'
import { useActiveScribeSession } from '@/pages/Call/hooks/useActiveScribeSession'
import { confirmDiscardScribeSession } from '@/pages/Call/hooks/confirmDiscardScribeSession'
import { useLiveTranscription, type LiveCaption } from '@/pages/Call/hooks/useLiveTranscription'
import { useFinalizeScribe, type FinalizeScribeResult } from '@/pages/Call/hooks/useFinalizeScribe'
import { useScribeFormBridge } from '@/pages/Call/context/ScribeFormBridgeContext'
import { ScribeDraftGenerationError } from '@/pages/Call/hooks/scribeErrors'
import {
  confirmDraftDisclaimer,
  persistDraftBeforePrompt,
  revealDraft,
  toStructuredDraft,
} from '@/pages/Call/hooks/applyDraftAndReveal'
import { logError } from '@/utils/errorUtils'
import { ZoomPanelHeader } from './ZoomPanelHeader'
import { PatientVideoFrame } from './PatientVideoFrame'
import { PanelBtn } from './PanelBtn'
import { AIScribeSection } from './AIScribeSection'
import { SessionStatusBar } from './SessionStatusBar'
import { ChatDrawer } from './ChatDrawer'
import { DoctorObservations } from './DoctorObservations'
import { AskAssistantSection } from './AskAssistantSection'
import { VisitGuidanceSection, type HiddenTopics } from './VisitGuidanceSection'
import { readObservationsFromStorage, clearObservationsFromStorage } from './observationsStorage'
import { EndSessionPrompt } from './EndSessionPrompt'
import { CallEndedNotice } from './CallEndedNotice'
import { ConversationTooShortModal } from './ConversationTooShortModal'
import { countTranscriptWords } from './transcriptWordCount'
import { TranscriptStream } from './TranscriptStream'
import { ZoomPanelPreJoin } from './ZoomPanelPreJoin'
import type { ScribeSourceLanguage } from '@behuman/core-contract'
import { useZoomPanelWidthReservation } from './useZoomPanelWidthReservation'
import { ExistingNoteNotice } from './ExistingNoteNotice'
import { DraftRecoveryNotice, type DraftRecoveryProps } from './DraftRecoveryNotice'
import { initialsOf } from './initials'
import { scribeText, SCRIBE_ACCENT } from './scribeText'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'

const DEFAULT_PANEL_WIDTH = 380
// The default is also the floor — the panel never opens or drags narrower.
const MIN_PANEL_WIDTH = DEFAULT_PANEL_WIDTH
const MAX_PANEL_WIDTH = 700

// Bounds for the "expanded" view. We scale to ~55% of viewport so the panel
// gets meaningfully bigger on wide monitors without quite half-covering the
// screen, then clamp to a sensible floor/ceiling so the expansion always
// feels different from the rail mode but never engulfs the chart on smaller
// laptops.
const EXPANDED_PANEL_MIN = 560
const EXPANDED_PANEL_MAX = 960
const EXPANDED_PANEL_VIEWPORT_RATIO = 0.55

// Soft gate: under this many captured words, End warns the doctor (never blocks).
const SCRIBE_MIN_WORDS = 30

// End-of-generation hand-off. The hold lets the amber→green completion register
// before the acknowledgement modal; the fade matches EndSessionPrompt's
// `transition-opacity duration-300` so the overlay is gone, not cut.
const DRAFT_READY_HOLD_MS = 600
const PROMPT_FADE_MS = 300
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Fullscreen column split — handled by `react-resizable-panels` (see
// `PanelGroup` in the render). The library owns the drag math, keyboard
// support, and persistence. We declare default + min/max sizes here so
// they're documented near the rest of the panel sizing constants.
const COLUMN_PANEL_GROUP_ID = 'zoom-panel-fullscreen-split'
const COLUMN_DEFAULT_LEFT_PCT = 60
const COLUMN_MIN_LEFT_PCT = 30
const COLUMN_MAX_LEFT_PCT = 80

function computeExpandedWidth(): number {
  if (typeof window === 'undefined') return EXPANDED_PANEL_MIN
  const target = window.innerWidth * EXPANDED_PANEL_VIEWPORT_RATIO
  return Math.min(EXPANDED_PANEL_MAX, Math.max(EXPANDED_PANEL_MIN, target))
}

// Vertical resize range for the patient-video frame. The transcript stream is
// `flex-1` underneath, so shrinking the video reclaims space for the live
// transcript and growing it gives the doctor a bigger participant view. Range
// is intentionally wide — clinicians can collapse the video almost to a strip
// when they're focused on transcript review.
/** Empty draft — the write-manually path runs the no-AI-signal handoff. */
const EMPTY_FINALIZE_RESULT: FinalizeScribeResult = {
  summary: undefined,
  conditions: [],
  procedures: [],
  medications: [],
  observations: [],
  allergies: [],
  serviceRequests: [],
  billing: undefined,
  sessionStartedAt: null,
}

// Rail allows collapsing to a strip for transcript review; fullscreen keeps
// the video a meaningful size.
const MIN_VIDEO_HEIGHT_RAIL = 80
const MIN_VIDEO_HEIGHT_FULLSCREEN = 280
// Rail-mode cap; the fullscreen cap is measured from the DOM (computeVideoHeightCap).
const MAX_VIDEO_HEIGHT_RAIL = 520

export type ZoomPanelProps = {
  /**
   * Kept for API compatibility with existing callers (CallPage,
   * EncounterWorkspace). Doesn't influence width — the new spec uses a
   * single default width — but is parked on the DOM as `data-parent-mode`
   * for diagnostic purposes.
   */
  parentMode: 'page' | 'side-panel'
  /** Patient FHIR id — drives §6 Visit Guidance (Risks & Gaps topics). */
  patientId?: string
  /** Patient display name shown in §3 and the video chip. */
  patientName?: string
  /** Provider display name shown in §3 ("Wendolin Garcia, FNP"). */
  providerName?: string
  /** Visit type shown in §3 ("Virtual Visit"). */
  visitType?: string
  /**
   * FHIR Encounter id — drives the doctor-observations localStorage key so
   * the scratchpad survives an interrupted-visit resume (which swaps the
   * scribe session id). Optional; falls back to no persistence when absent.
   */
  encounterId?: string
  /**
   * Interrupted-visit recovery — prior transcript lines fetched from the
   * server and seeded into the live transcript pane on a resume. Undefined on
   * a normal fresh join.
   */
  recoveredCaptions?: LiveCaption[]
  /**
   * Page-mode "Join Session" pre-join surface. When true AND the call hasn't
   * been joined yet AND `parentMode === 'page'`, the panel renders a compact
   * pre-join slot (`<ZoomPanelPreJoin>`) in place of `null` so the doctor who
   * landed on `/call/:encounterId` (e.g. from the schedule) has an explicit
   * way into the call. CallPage owns the gating (see `showJoinPrompt` there).
   * Ignored in side-panel mode, which hands off to page mode to join.
   */
  showJoinPrompt?: boolean
  /**
   * Click handler for the pre-join "Join Session" CTA. CallPage points this at
   * its single `requestJoin` entry (recovery probe → fresh join). No-op
   * when absent.
   */
  onJoin?: () => void
  /**
   * Join-in-flight indicator (page mode only, like `showJoinPrompt`). While
   * a join runs — Zoom SDK chunk download → signature fetch → init/join
   * handshake, several seconds — the panel renders the pre-join slot in its
   * "Connecting…" variant instead of `null`, so the rail appears the moment
   * the join starts rather than only after it lands.
   */
  connecting?: boolean
  /** PD-1105 — reopened encounter already has a Progress Note; render the notice (edit/view + new visit) instead of the join slot. Page mode only. */
  showExistingNote?: boolean
  /** Existing note is locked (`final`) → notice shows "View" instead of "Edit". */
  isExistingNoteLocked?: boolean
  /** Open the existing note (editor when unlocked, viewer when locked). */
  onViewEditNote?: () => void
  /** Start a fresh ad-hoc visit for this patient. */
  onStartNewVisit?: () => void
  /** AI-draft actions for an UNLOCKED existing note (PD-1105 notice). Absent → hidden. */
  onLoadAiDraft?: () => void
  onRegenerateNote?: () => void
  aiActionBusy?: 'load' | 'regenerate' | null
  /**
   * Set when the encounter has a recoverable scribe artifact but no note —
   * takes the pre-join slot so the doctor is offered the work back instead of a
   * rejoin. Null in every other state.
   */
  draftRecovery?: DraftRecoveryProps | null
  /** PD-1129 backup method — open the empty progress-note pane without joining. */
  onWriteManualNote?: () => void
  /** PD-1600 — the patient's stated language, the pre-join picker's default. */
  patientLanguage?: ScribeSourceLanguage
}

export function ZoomPanel({
  parentMode,
  patientId,
  patientName,
  providerName,
  visitType,
  encounterId,
  recoveredCaptions,
  showJoinPrompt,
  onJoin,
  connecting,
  showExistingNote,
  isExistingNoteLocked,
  onViewEditNote,
  onStartNewVisit,
  onLoadAiDraft,
  onRegenerateNote,
  aiActionBusy,
  draftRecovery,
  onWriteManualNote,
  patientLanguage,
}: ZoomPanelProps) {
  const {
    isJoined,
    scribeState,
    patientPresence,
    staffPresence,
    staffDisplayName,
    isAudioMuted,
    isVideoMuted,
    toggleMute,
    toggleVideo,
    canUseVirtualBackground,
    isVirtualBackgroundOn,
    toggleVirtualBackground,
    leaveSession,
    scribeSessionId,
    isLocalUserPhysician,
    isLocalUserManager,
    chatMessages,
    assistantThreadPath,
    setAssistantThreadPath,
    encounterLanguage,
    setEncounterLanguage,
  } = useCallSession()
  // PD-1600 — the patient's stated language seeds the picker once per patient,
  // before the call. The picker is usable while the Patient is still loading, so
  // seeding must also yield to a pick the provider already made: the language
  // reaches Zoom's transcription, and overwriting it transcribes the visit in the
  // wrong one. A new patient clears the pick, since the old default no longer applies.
  const languageSeededForRef = useRef<string | undefined>(undefined)
  const languageTouchedForRef = useRef<string | undefined>(undefined)
  const chooseEncounterLanguage = useCallback(
    (language: ScribeSourceLanguage) => {
      languageTouchedForRef.current = patientId
      setEncounterLanguage(language)
    },
    [patientId, setEncounterLanguage],
  )
  useEffect(() => {
    if (isJoined) return
    if (languageSeededForRef.current === patientId || languageTouchedForRef.current === patientId) {
      return
    }
    // `JOIN` carries `encounterLanguage` forward, so a patient switch must drop
    // the previous one immediately rather than wait for the new patient to load
    // — joining in that window would transcribe in the prior patient's language.
    // Seeding is only recorded once the preference actually arrives, so the real
    // default still lands when it does.
    setEncounterLanguage(patientLanguage ?? 'en')
    if (patientLanguage) languageSeededForRef.current = patientId
  }, [isJoined, patientLanguage, patientId, setEncounterLanguage])
  const { isStarting, start, pause, resume } = useScribeSession()
  const { discardSession } = useActiveScribeSession()
  const {
    finalize,
    retryDraft,
    abandonDraftRetry,
    draftRetryAvailable,
    isFinalizing,
    isGeneratingDraft,
    draftProgress,
  } = useFinalizeScribe()
  const { captions, isStreaming, flushPending } = useLiveTranscription({
    seedCaptions: recoveredCaptions,
  })
  const bridge = useScribeFormBridge()

  const [width, setWidth] = useState<number>(DEFAULT_PANEL_WIDTH)
  // "Expanded" mode toggles the panel to a much wider view for focused
  // call-watching (e.g. examining the patient on video). When `isExpanded`
  // is true, `effectiveWidth` below ignores the drag-resize width and uses
  // a viewport-scaled value instead. Toggle back collapses to the previous
  // dragged width.
  const [isExpanded, setIsExpanded] = useState(false)
  const [expandedWidth, setExpandedWidth] = useState<number>(() => computeExpandedWidth())
  // Doctor's in-call observations — free-text the AI considers on End
  // Session. Hydrated from localStorage whenever the scribe session id
  // becomes known (page reload mid-call recovers the previous notes).
  const [doctorObservations, setDoctorObservations] = useState<string>('')
  const [observationsCollapsed, setObservationsCollapsed] = useState<boolean>(true)
  // §6/§7 space split. The transcript starts collapsed so Visit Guidance owns
  // the rail's vertical space; both live here (not in the sections) because
  // the rail↔fullscreen layout switch remounts the transcript column.
  const [guidanceCollapsed, setGuidanceCollapsed] = useState<boolean>(false)
  const [transcriptCollapsed, setTranscriptCollapsed] = useState<boolean>(true)
  // §9 Ask BeAssistant — collapsed by default; an untouched section never
  // starts a conversation (the thread starts lazily on the first question).
  const [assistantCollapsed, setAssistantCollapsed] = useState<boolean>(true)
  // Dismissed Visit Guidance topics, scoped to one report id (topic ids are
  // positional per report, so a regenerated report resets the set).
  const [hiddenTopics, setHiddenTopics] = useState<HiddenTopics>(null)
  // Guards the section's forced pre-feature rebuild to once per call session —
  // inside the (remounting) section it would re-fire per layout switch.
  const guidanceRebuildKickedRef = useRef(false)
  // Fresh join = fresh guidance state (the autoStartedRef convention).
  useEffect(() => {
    if (isJoined) return
    guidanceRebuildKickedRef.current = false
    setHiddenTopics(null)
    setGuidanceCollapsed(false)
    setTranscriptCollapsed(true)
    setAssistantCollapsed(true)
  }, [isJoined])
  useEffect(() => {
    // Hydrate from the encounter-scoped key (stable across an interrupted-visit
    // resume that swaps the scribe session id).
    if (!encounterId) return
    setDoctorObservations(readObservationsFromStorage(encounterId))
  }, [encounterId])

  const medplum = useMedplum()
  const [isSendingLink, setIsSendingLink] = useState(false)
  const handleSendAppointmentLink = useCallback(async () => {
    if (!encounterId) return
    setIsSendingLink(true)
    try {
      await medplum.post('auth/magic-link/send-code', { encounterId })
      showNotification({ color: 'green', message: 'Appointment link sent to patient' })
    } catch (err) {
      logError('zoom-panel', 'Send appointment link failed', err)
      showNotification({ color: 'red', message: 'Failed to send appointment link' })
    } finally {
      setIsSendingLink(false)
    }
  }, [medplum, encounterId])
  // `null` means "use natural 4:3 aspect ratio" — i.e. the user has not dragged
  // the vertical handle yet. Once they drag, we lock in a pixel height.
  const [videoHeight, setVideoHeight] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [endPromptOpen, setEndPromptOpen] = useState(false)
  const [tooShortOpen, setTooShortOpen] = useState(false)
  const transcriptWordCount = useMemo(() => countTranscriptWords(captions), [captions])
  // In-call chat drawer (slide-out from the right of the viewport). Bound to
  // the Chat PanelBtn in the controls row. The Zoom SDK `chat-on-message`
  // listener lives in CallSessionProvider so messages survive opening and
  // closing the drawer — this flag only controls visibility.
  const [isChatOpen, setIsChatOpen] = useState(false)
  // Unread badge = OTHERS' messages beyond what was on screen when the chat
  // was last open — own messages never count.
  const [chatSeenCount, setChatSeenCount] = useState(0)
  const chatOthersCount = chatMessages.filter((m) => !m.isOwn).length
  useEffect(() => {
    if (isChatOpen) setChatSeenCount(chatOthersCount)
  }, [isChatOpen, chatOthersCount])
  const chatUnread = isChatOpen ? 0 : chatOthersCount - chatSeenCount
  // FE-4 — Local spinner-keep-up flag for the post-finalize auto-submit
  // window. `useFinalizeScribe.isGeneratingDraft` flips false the instant the
  // `/generate-draft` POST resolves, but at that point we still need the
  // Progress-Note form to auto-submit and navigate away. Without this flag the
  // EndSessionPrompt would drop the spinner overlay and reveal the edit form
  // for a flash before the navigation completed.
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false)
  // Keeps the post-call generating/retry panel up across leave → generate →
  // submit (the in-call prompt unmounts the moment the call leaves).
  const [isProcessingNote, setIsProcessingNote] = useState(false)
  // Hand-off beats: the panel completes (amber→green) and holds, then the
  // acknowledgement modal lands on a settled backdrop, then the overlay fades
  // as the note is revealed underneath.
  const [draftReady, setDraftReady] = useState(false)
  const [promptClosing, setPromptClosing] = useState(false)
  // Sticks after a completed hand-off so the rail rests on "Call ended" instead
  // of falling back to the pre-join "Join Session" CTA.
  const [callEnded, setCallEnded] = useState(false)
  const [callEndedDismissed, setCallEndedDismissed] = useState(false)

  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null)
  const videoDragStateRef = useRef<{
    startY: number
    startHeight: number
    cap: number
    min: number
  } | null>(null)
  // Rail-mode video height remembered across a fullscreen round-trip.
  const railVideoHeightRef = useRef<number | null>(null)
  // Ref to the live video-frame DOM node so the vertical drag handle can read
  // the natural 4:3 height on the FIRST drag (before `videoHeight` is set).
  // Without this, the first drag would snap from "auto 4:3" to whatever pixel
  // value the pointer's Y-delta lands on, which feels jumpy.
  const videoFrameRef = useRef<HTMLDivElement | null>(null)

  // §1 timer — runs once the panel mounts (i.e. once isJoined flips true).
  useEffect(() => {
    if (!isJoined) {
      setElapsed(0)
      return
    }
    const interval = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(interval)
  }, [isJoined])

  // Auto-start the scribe as soon as the call connects — the doctor shouldn't
  // have to click a second "Start AI Scribe" button after already joining.
  // The `JOIN` reducer action resets scribeState to 'idle' on every fresh
  // join, so this fires exactly once per call. After Stop / finalize the ref
  // stays true for the rest of the join cycle, so we don't auto-restart
  // behind the doctor's back — a manual restart is still available via the
  // idle prompt. On the next fresh join, `isJoined` flips false → true and
  // the ref resets.
  //
  // Interrupted-visit resume: a resume-aware JOIN seeds `scribeState` to
  // 'streaming' and adopts the recovered `scribeSessionId`, so the
  // `scribeState !== 'idle'` guard below makes this effect SKIP the auto-start
  // — the recovered session is adopted, not replaced with a fresh one.
  const autoStartedRef = useRef(false)
  useEffect(() => {
    if (!isJoined) {
      autoStartedRef.current = false
      return
    }
    // Scribe runs only on the treating physician's client — gate BEFORE the
    // started-ref flip so an unresolved/non-physician skip stays re-runnable.
    if (isLocalUserPhysician !== true) return
    if (autoStartedRef.current) return
    if (scribeState !== 'idle') return
    if (isStarting) return
    autoStartedRef.current = true
    void start()
  }, [isJoined, scribeState, isStarting, start, isLocalUserPhysician])

  // Drag-resize — vanilla pointer-event handlers (no library) for the §1+
  // left-edge resize handle. Range 380-700px; in-memory only.
  const onPointerMove = useCallback((e: PointerEvent) => {
    const drag = dragStateRef.current
    if (!drag) return
    // Handle sits on the LEFT edge — dragging right shrinks, left grows.
    const next = drag.startWidth - (e.clientX - drag.startX)
    setWidth(Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, next)))
  }, [])

  const onPointerUp = useCallback(() => {
    dragStateRef.current = null
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [onPointerMove])

  const onHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragStateRef.current = { startX: e.clientX, startWidth: width }
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', onPointerUp)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
    },
    [width, onPointerMove, onPointerUp],
  )

  // Video drag height cap: the rows below the frame (handle/controls) must
  // never be pushed off-screen, so fullscreen measures the real chrome.
  const computeVideoHeightCap = useCallback((): number => {
    const frame = videoFrameRef.current
    const column = frame?.parentElement
    if (isExpanded && frame && column) {
      let chrome = 0
      for (const child of Array.from(column.children)) {
        if (child !== frame) chrome += (child as HTMLElement).offsetHeight
      }
      return Math.max(MIN_VIDEO_HEIGHT_FULLSCREEN, column.clientHeight - chrome)
    }
    return MAX_VIDEO_HEIGHT_RAIL
  }, [isExpanded])

  const onVideoPointerMove = useCallback((e: PointerEvent) => {
    const drag = videoDragStateRef.current
    if (!drag) return
    const next = drag.startHeight + (e.clientY - drag.startY)
    setVideoHeight(Math.max(drag.min, Math.min(drag.cap, next)))
  }, [])

  // Fullscreen sizes the video to everything that fits (cap measured after the
  // expanded layout paints); leaving restores the pre-fullscreen rail height.
  useEffect(() => {
    if (!isExpanded) return
    setVideoHeight((h) => {
      railVideoHeightRef.current = h
      return h
    })
    const raf = requestAnimationFrame(() => setVideoHeight(computeVideoHeightCap()))
    // Re-clamp when the column resizes — window resize AND the column-divider
    // drag (which can also wrap the controls row, growing the chrome).
    const clamp = () => {
      const cap = computeVideoHeightCap()
      setVideoHeight((h) => (h == null ? h : Math.min(h, cap)))
    }
    const column = videoFrameRef.current?.parentElement
    const observer = new ResizeObserver(clamp)
    if (column) observer.observe(column)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      const saved = railVideoHeightRef.current
      setVideoHeight(saved == null ? null : Math.min(saved, MAX_VIDEO_HEIGHT_RAIL))
    }
  }, [isExpanded, computeVideoHeightCap])

  const onVideoPointerUp = useCallback(() => {
    videoDragStateRef.current = null
    window.removeEventListener('pointermove', onVideoPointerMove)
    window.removeEventListener('pointerup', onVideoPointerUp)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [onVideoPointerMove])

  const onVideoHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      // Seed from the live DOM height the first time, so the first drag is
      // smooth (no jump from auto-4:3 to wherever the pointer lands). The cap
      // is measured once per drag — sibling chrome can't change mid-drag.
      const seed =
        videoHeight ??
        videoFrameRef.current?.getBoundingClientRect().height ??
        Math.round(width * 0.75)
      videoDragStateRef.current = {
        startY: e.clientY,
        startHeight: seed,
        cap: computeVideoHeightCap(),
        min: isExpanded ? MIN_VIDEO_HEIGHT_FULLSCREEN : MIN_VIDEO_HEIGHT_RAIL,
      }
      window.addEventListener('pointermove', onVideoPointerMove)
      window.addEventListener('pointerup', onVideoPointerUp)
      document.body.style.cursor = 'ns-resize'
      document.body.style.userSelect = 'none'
    },
    [videoHeight, width, isExpanded, computeVideoHeightCap, onVideoPointerMove, onVideoPointerUp],
  )

  // Defensive cleanup if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointermove', onVideoPointerMove)
      window.removeEventListener('pointerup', onVideoPointerUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [onPointerMove, onPointerUp, onVideoPointerMove, onVideoPointerUp])

  // §4 mute + video — the provider owns the Zoom MediaStream, so the panel
  // is now a presentational consumer. Errors from the toggles are already
  // captured in the provider's `logError` calls.

  // Scribe runs only on the treating physician's client — gates every manual start path too.
  const canStartScribe = isLocalUserPhysician === true
  // Managers (care coordinators / call-center managers) never see the scribe,
  // transcript, or observations surfaces. Fail-open: null/false ⇒ shown.
  const isManagerUser = isLocalUserManager === true

  // §1 + §4 — single dispatch for the Scribe button (header + controls row).
  const onScribeAction = useCallback(() => {
    if (scribeState === 'idle') {
      if (canStartScribe) void start()
      return
    }
    if (scribeState === 'streaming') {
      pause()
      return
    }
    if (scribeState === 'paused') {
      resume()
    }
  }, [scribeState, start, pause, resume, canStartScribe])

  // §1 + §8 — End button. Opens the confirm prompt only when there's something
  // to save (any captured transcript OR a still-running/paused scribe). When
  // there's nothing — no captions AND the scribe is idle (never started, or
  // already stopped with an empty buffer) — there's no Progress Note to create
  // and nothing to confirm, so we skip the prompt and tear the call down
  // directly. Mirrors the mockup at ChartPage.tsx:5219-5225, where the
  // no-transcript branch calls `onEnd()` (session teardown) instead of opening
  // the prompt. Here `leaveSession()` is that teardown.
  const onEndClick = useCallback(() => {
    // Review gate: nothing captured (muted) or too thin to draft → offer Generate/Discard (PD-1111).
    if (transcriptWordCount < SCRIBE_MIN_WORDS && scribeState !== 'idle') {
      setTooShortOpen(true)
      return
    }
    if (captions.length > 0 || scribeState !== 'idle') {
      setEndPromptOpen(true)
      return
    }
    // Nothing captured and scribe idle — no note to create, no prompt to show.
    // End the Zoom session directly.
    void (async () => {
      try {
        await leaveSession()
      } catch (err) {
        logError('zoom-panel', 'End call (no transcript) leaveSession failed', err)
      }
    })()
  }, [captions.length, transcriptWordCount, scribeState, leaveSession])

  // §8 — Shared post-draft handoff. Push the structured AI draft (LD-1) through
  // the bridge in one atomic call, or — when there's no AI signal — submit the
  // carry-forward draft so no empty edit form is left behind. `{ autoSubmit:
  // true }` is awaited so the "Generating…" overlay stays up through the
  // populate → POST → navigate sequence (no flash of the edit form), then Zoom
  // is torn down (End ends the whole call). Reused by the End-Session flow, the
  // draft retry, and the write-manually path.
  const applyDraftResultAndLeave = useCallback(
    async (result: FinalizeScribeResult) => {
      // Generation has finished by the time we reach here (finalize resolved), so
      // close the "Generating Progress Note" overlay BEFORE any confirm — the
      // review popup must never appear over a still-loading progress bar (PD-1139).
      setIsProcessingNote(false)
      // PD-1378 — finalize refused: another run owns the generation. Applying
      // this empty result would auto-submit a blank note next to the real one,
      // so leave instead (the manual path's EMPTY_FINALIZE_RESULT never carries
      // this flag).
      if (result.alreadyGenerating) {
        setEndPromptOpen(false)
        setCallEnded(true)
        await leaveSession()
        return
      }
      // The Progress Note form never mounts for managers — the bridge steps
      // below would hang. The draft is already stored server-side; the
      // physician opens it via the draft-recovery rail.
      if (isManagerUser) {
        showNotification({
          color: 'gray',
          message: 'The note draft is saved for the treating physician to review and sign.',
        })
        setEndPromptOpen(false)
        setCallEnded(true)
        await leaveSession()
        return
      }
      const prepared = toStructuredDraft(result)
      await persistDraftBeforePrompt(bridge, prepared, 'zoom-panel')
      // Scratchpad consumed by the AI — clear it so it doesn't carry forward.
      clearObservationsFromStorage(encounterId)
      // Completion beat before the acknowledgement, AI path only: the panel
      // sweeps amber→green and holds so the work visibly finishes (and no
      // spinner keeps turning while we wait on a human).
      if (prepared.hasAiSignal) {
        setDraftReady(true)
        await sleep(DRAFT_READY_HOLD_MS)
      }
      await confirmDraftDisclaimer(result, prepared.hasAiSignal)
      // Fade the overlay out over the note being revealed underneath.
      setPromptClosing(true)
      await sleep(PROMPT_FADE_MS)
      setIsAutoSubmitting(true)
      try {
        await revealDraft(bridge, prepared)
      } catch (submitErr) {
        logError('zoom-panel', 'Submit Progress Note failed', submitErr)
      } finally {
        setIsAutoSubmitting(false)
      }
      setEndPromptOpen(false)
      setPromptClosing(false)
      setDraftReady(false)
      setCallEnded(true)
      await leaveSession()
    },
    [bridge, encounterId, isManagerUser, leaveSession],
  )

  const onCreateProgressNote = useCallback(async () => {
    // Leave fullscreen first so the overlay + edit form render in the normal
    // rail layout, not behind the fullscreen overlay.
    setIsExpanded(false)
    setIsProcessingNote(true)
    try {
      // leaveSession runs as finalize's onPersisted hook: the call ends as soon as
      // the transcript is saved, so the draft generates off-call.
      const result = await finalize(
        'with-draft',
        patientId,
        doctorObservations,
        undefined,
        undefined,
        leaveSession,
        flushPending,
      )
      await applyDraftResultAndLeave(result)
    } catch (err) {
      // Draft failed but the call already ended — keep the post-call retry panel
      // up (the hook owns `draftRetryAvailable`).
      if (err instanceof ScribeDraftGenerationError) {
        setIsAutoSubmitting(false)
        setDraftReady(false)
        setPromptClosing(false)
        return
      }
      logError('zoom-panel', 'Create Progress Note flow failed', err)
      setIsAutoSubmitting(false)
      setEndPromptOpen(false)
      setDraftReady(false)
      setPromptClosing(false)
      await leaveSession()
    } finally {
      setIsProcessingNote(false)
    }
  }, [
    finalize,
    patientId,
    doctorObservations,
    applyDraftResultAndLeave,
    leaveSession,
    flushPending,
  ])

  // Keep recording is a plain dismiss — capture is never paused at End-click.
  const onKeepRecording = useCallback(() => setTooShortOpen(false), [])

  const onGenerateAnyway = useCallback(() => {
    setTooShortOpen(false)
    setEndPromptOpen(true)
    void onCreateProgressNote()
  }, [onCreateProgressNote])

  const onDiscardTooShort = useCallback(() => {
    // Exit fullscreen so the destructive confirm isn't shown over a fullscreen call.
    setIsExpanded(false)
    setTooShortOpen(false)
    confirmDiscardScribeSession({
      lineCount: captions.length,
      onConfirm: async () => {
        try {
          if (encounterId && scribeSessionId) await discardSession(encounterId, scribeSessionId)
        } catch (err) {
          logError('zoom-panel', 'Too-short discard failed', err)
        }
        try {
          await leaveSession()
        } catch (err) {
          logError('zoom-panel', 'Too-short discard leaveSession failed', err)
        }
      },
      onCancel: () => setTooShortOpen(true),
    })
  }, [captions.length, scribeSessionId, encounterId, discardSession, leaveSession])

  const onRetryDraft = useCallback(async () => {
    setIsProcessingNote(true)
    try {
      const result = await retryDraft()
      await applyDraftResultAndLeave(result)
    } catch (err) {
      if (err instanceof ScribeDraftGenerationError) {
        setIsAutoSubmitting(false)
        setDraftReady(false)
        setPromptClosing(false)
        return
      }
      logError('zoom-panel', 'Retry AI draft failed', err)
      setIsAutoSubmitting(false)
      setEndPromptOpen(false)
      setDraftReady(false)
      setPromptClosing(false)
      await leaveSession()
    } finally {
      setIsProcessingNote(false)
    }
  }, [retryDraft, applyDraftResultAndLeave, leaveSession])

  const onWriteManually = useCallback(async () => {
    abandonDraftRetry()
    await applyDraftResultAndLeave(EMPTY_FINALIZE_RESULT)
  }, [abandonDraftRetry, applyDraftResultAndLeave])

  // Auto-close the end-session prompt once scribeState returns to idle.
  //
  // FE-4 — Skip the auto-close while the post-finalize auto-submit is still in
  // flight. `useFinalizeScribe` flips scribeState back to 'idle' as soon as
  // `/generate-draft` resolves, but we need the End-Session spinner overlay
  // to stay up until the Progress Note POST completes and navigation fires.
  // Otherwise the overlay disappears and the doctor sees the populated edit
  // form for a flash before the read-only viewer.
  useEffect(() => {
    if (scribeState === 'idle' && !isAutoSubmitting) {
      setEndPromptOpen(false)
    }
  }, [scribeState, isAutoSubmitting])

  // Effective width — when expanded, bypass the drag-resize bounds and use
  // the viewport-scaled width. The drag handle still updates `width`
  // underneath so toggling back restores whatever the doctor had set.
  const effectiveWidth = isExpanded ? expandedWidth : width

  // Recompute the expanded width on window resize so the panel rescales
  // when the doctor pops a monitor or changes the browser size mid-call.
  // Only attaches the listener while expanded — no churn otherwise.
  useEffect(() => {
    if (!isExpanded) return
    const onResize = () => setExpandedWidth(computeExpandedWidth())
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isExpanded])

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  // End paths that bypass the toggle (e.g. a coordinator's direct End) must
  // not strand the post-call rail at fullscreen width with no resize handle.
  useEffect(() => {
    if (!isJoined) setIsExpanded(false)
  }, [isJoined])

  // Fullscreen has room for the scratchpad — open it there; the rail reverts
  // to the slim collapsed header. Same for the transcript (the 60/40 split
  // fits guidance and transcript side by side).
  useEffect(() => {
    setObservationsCollapsed(!isExpanded)
    setTranscriptCollapsed(!isExpanded)
  }, [isExpanded])

  // Fullscreen spans 100vw, which includes the page scrollbar strip — lock
  // page scroll while expanded so that scrollbar doesn't overlay the panel.
  useEffect(() => {
    if (!isExpanded) return
    const prevHtml = document.documentElement.style.overflow
    const prevBody = document.body.style.overflow
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      document.documentElement.style.overflow = prevHtml
      document.body.style.overflow = prevBody
    }
  }, [isExpanded])

  // When toggling fullscreen, force a synthetic `resize` event so any
  // ResizeObserver-driven CSS vars (notably `--patient-header-height` and
  // `--recent-bar-height` from the sticky chart chrome) re-measure. While
  // the chart was hidden behind the fullscreen overlay, those observers
  // may have missed layout shifts, leaving stale measurements that put
  // the panel's `top: var(--chart-content-top)` slightly too high on the
  // way back — the action bar then visibly clips the panel header. The
  // resize event nudges every observer to re-publish its var fresh.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('resize'))
  }, [isExpanded])

  // Page-mode pre-join state: the doctor landed on `/call/:encounterId` (e.g.
  // from the schedule) without a live call yet. Render the compact "Join
  // Session" slot in the same fixed footprint as the rail so the chart reflows
  // identically whether the call is live or merely about to be joined. Only in
  // page mode — the side-panel flow creates the Encounter then hands off here.
  const showPreJoin =
    !isJoined &&
    !callEnded &&
    !callEndedDismissed &&
    Boolean(showJoinPrompt) &&
    parentMode === 'page'
  // Join-in-flight sibling of the pre-join slot — same footprint, spinner
  // variant. Mutually exclusive with `showPreJoin`: CallPage hides the prompt
  // while `joinConnecting` is true.
  const showConnecting = !isJoined && Boolean(connecting) && parentMode === 'page'
  // PD-1105 — existing-note notice occupies the same slot as the pre-join.
  const showNoteNotice = !isJoined && Boolean(showExistingNote) && parentMode === 'page'
  // Draft/transcript recovery shares that slot too — CallPage guarantees only
  // one of these is set at a time.
  const showDraftRecovery = !isJoined && Boolean(draftRecovery) && parentMode === 'page'

  // The rail is position:fixed, so publish its width as `--zoom-panel-width` on
  // <html> for the shell + PatientChartLayout to reserve. Reserve in EVERY state
  // a rail is shown — not just `isJoined` — else the chart drops the gap and
  // content slides under the rail. Fullscreen reserves 0; the hook ref-counts so
  // a leaving rail can't wipe another rail's reservation.
  const railVisible =
    isJoined ||
    isProcessingNote ||
    draftRetryAvailable ||
    (callEnded && !callEndedDismissed) ||
    showNoteNotice ||
    showDraftRecovery ||
    showPreJoin ||
    showConnecting
  useZoomPanelWidthReservation(railVisible ? (isExpanded && isJoined ? 0 : effectiveWidth) : null)

  if (!isJoined) {
    // The note generates off-call, so keep a minimal rail panel hosting the
    // generating/retry UI (EndSessionPrompt) after the live call panel is gone.
    if (isProcessingNote || draftRetryAvailable || (callEnded && !callEndedDismissed)) {
      return (
        <aside
          data-testid="zoom-panel-postcall"
          className="fixed flex flex-col overflow-hidden bg-[#0f172a] text-slate-200"
          style={{
            top: CHART_CONTENT_TOP_VAR,
            right: 0,
            width: `${effectiveWidth}px`,
            height: CHART_CONTENT_HEIGHT_VAR,
            zIndex: 'var(--z-scribe)' as unknown as number,
            borderLeft: '1px solid #1e293b',
          }}
        >
          {callEnded && !isProcessingNote && !draftRetryAvailable ? (
            <CallEndedNotice onDismiss={() => setCallEndedDismissed(true)} />
          ) : (
            <EndSessionPrompt
              isFinalizing={false}
              isGeneratingDraft={isProcessingNote}
              draftProgress={draftProgress}
              draftReady={draftReady}
              closing={promptClosing}
              draftError={draftRetryAvailable}
              onConfirmEnd={() => {}}
              onCancel={() => {}}
              onRetryDraft={() => void onRetryDraft()}
              onWriteManually={() => void onWriteManually()}
            />
          )}
        </aside>
      )
    }
    if (showNoteNotice) {
      return (
        <ExistingNoteNotice
          width={effectiveWidth}
          patientName={patientName}
          patientId={patientId}
          isLocked={isExistingNoteLocked}
          onViewEditNote={onViewEditNote}
          onStartNewVisit={onStartNewVisit}
          onLoadAiDraft={onLoadAiDraft}
          onRegenerateNote={onRegenerateNote}
          aiActionBusy={aiActionBusy}
        />
      )
    }
    if (showDraftRecovery && draftRecovery) {
      return (
        <DraftRecoveryNotice width={effectiveWidth} patientName={patientName} {...draftRecovery} />
      )
    }
    return showPreJoin || showConnecting ? (
      <ZoomPanelPreJoin
        width={effectiveWidth}
        patientName={patientName}
        visitType={visitType}
        onJoin={onJoin}
        connecting={showConnecting}
        onWriteManualNote={onWriteManualNote}
        language={encounterLanguage}
        onLanguageChange={chooseEncounterLanguage}
      />
    ) : null
  }

  const patientInitials = initialsOf(patientName) || 'PT'
  const providerInitials = initialsOf(providerName) || 'DR'
  const displayPatientName = patientName ?? 'Patient'

  const isScribeActive = scribeState === 'streaming' || scribeState === 'paused'

  return (
    <aside
      data-testid="zoom-panel"
      data-parent-mode={parentMode}
      data-expanded={isExpanded ? 'true' : 'false'}
      // Position:
      //   - Default (rail): hug right edge, top below the chart's sticky
      //     chrome (Shell header + RECENT bar + patient header), height
      //     matches the chart content area. The chart reserves matching
      //     padding-right via `--zoom-panel-width` so nothing overlaps.
      //   - Expanded (fullscreen overlay): `inset: 0` covers the entire
      //     viewport — including the Shell header and the chart action
      //     bar. The chart is hidden behind the panel. The CSS-var
      //     reservation drops to `0px` (handled in the effect above) so
      //     the chart's layout doesn't compute against a 100vw padding.
      className="fixed flex flex-col overflow-hidden bg-[#0f172a] text-slate-200"
      style={
        {
          top: isExpanded ? 0 : CHART_CONTENT_TOP_VAR,
          right: 0,
          left: isExpanded ? 0 : 'auto',
          bottom: isExpanded ? 0 : 'auto',
          width: isExpanded ? '100vw' : `${effectiveWidth}px`,
          height: isExpanded ? '100vh' : CHART_CONTENT_HEIGHT_VAR,
          // Expanded sits above app chrome but BELOW Mantine's modal layer
          // (200) — confirms/disclaimers must stay clickable in fullscreen.
          zIndex: (isExpanded
            ? 'var(--z-scribe-expanded)'
            : 'var(--z-scribe)') as unknown as number,
          // Coral accent for the descendant resize handles' hover state —
          // drives `hover:bg-[var(--scribe-accent)]` from the shared token.
          '--scribe-accent': SCRIBE_ACCENT,
        } as React.CSSProperties
      }
    >
      {/* Drag handle — left edge. Hidden while expanded so dragging the
          edge doesn't appear to do anything (the dragged width is
          overridden by the expanded preset until the doctor toggles back).
          The handle owns the chart/panel seam (`border-l`) — a border on the
          aside itself would sit outside its `overflow-hidden` padding box,
          where the hover fill can't paint over it. */}
      {!isExpanded && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize scribe panel"
          onPointerDown={onHandlePointerDown}
          className="absolute top-0 left-0 z-30 h-full w-[5px] cursor-ew-resize border-l border-[#1e293b] transition-colors hover:border-l-transparent hover:bg-[var(--scribe-accent)]"
          data-testid="zoom-panel-resize-handle"
        />
      )}

      {/* §1 Header */}
      <ZoomPanelHeader
        width={effectiveWidth}
        elapsedSeconds={elapsed}
        scribeState={scribeState}
        patientPresence={patientPresence}
        isStarting={isStarting}
        onScribeAction={onScribeAction}
        onEnd={onEndClick}
        isExpanded={isExpanded}
        onToggleExpand={handleToggleExpand}
        hideScribe={isManagerUser}
      />

      {/* Body. Layout flips between:
            - Collapsed (default 380px rail): single-column — video, session,
              controls, scribe/transcript stacked vertically.
            - Expanded (~55% viewport): TWO columns — video + session +
              controls on the LEFT (60% of panel), scribe/transcript on the
              RIGHT (40%). Keeps the patient video big while preserving the
              live transcript view so the doctor can document during the
              call. Replaces the prior naive "just widen the panel" behavior
              which let the 4:3 video frame grow to ~720px tall and push
              everything else off-screen.
          The fragment below is structurally identical in both modes — only
          the wrapper around the two groups changes. */}
      {(() => {
        const videoColumn = (
          <>
            {/* §2 Patient video — wrapped so the vertical resize handle and
                the frame share the same DOM ancestor. The ref reads the
                natural 4:3 height on the first drag (before `videoHeight`
                is set) so the drag starts smoothly from where the user
                actually clicked. */}
            <div ref={videoFrameRef}>
              <PatientVideoFrame
                patientInitials={patientInitials}
                providerInitials={providerInitials}
                patientName={displayPatientName}
                isPatientMuted={false}
                isVideoOff={isVideoMuted}
                patientPresence={patientPresence}
                staffPresence={staffPresence}
                staffName={staffDisplayName ?? undefined}
                height={videoHeight ?? undefined}
              />
            </div>

            {/* §2b Vertical drag handle — sits between the video frame and
                the session-info row. Drag DOWN to grow the video, UP to
                shrink it (which expands the live transcript area
                underneath). */}
            <div
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize video frame"
              onPointerDown={onVideoHandlePointerDown}
              className="relative h-[5px] shrink-0 cursor-ns-resize bg-[#1e293b] transition-colors hover:bg-[var(--scribe-accent)]"
              data-testid="zoom-panel-video-resize-handle"
            />

            {/* §3b Session status bar — Provider / Patient / Scribe,
                always visible (PD-1067 item 7). */}
            <SessionStatusBar />

            {/* §4 Controls row — compact (icon-stacked) in rail mode,
                large pill row in fullscreen so the controls read clearly
                from the patient-watching distance. */}
            <div
              data-testid="zoom-panel-controls"
              className={
                isExpanded
                  ? 'flex shrink-0 flex-wrap items-center justify-center gap-3 border-b border-white/[0.07] px-4 py-3'
                  : 'flex shrink-0 items-center justify-between border-b border-white/[0.07] px-[2px] py-2'
              }
            >
              <PanelBtn
                icon={
                  isAudioMuted ? (
                    <IconMicrophoneOff size={isExpanded ? 18 : 15} />
                  ) : (
                    <IconMicrophone size={isExpanded ? 18 : 15} />
                  )
                }
                label={isAudioMuted ? 'Unmute' : 'Mute'}
                active={isAudioMuted}
                activeColor="#ef4444"
                onClick={() => void toggleMute()}
                testId="zoom-panel-btn-mute"
                large={isExpanded}
              />
              <PanelBtn
                icon={
                  isVideoMuted ? (
                    <IconVideoOff size={isExpanded ? 18 : 15} />
                  ) : (
                    <IconVideo size={isExpanded ? 18 : 15} />
                  )
                }
                label={isVideoMuted ? 'Start Video' : 'Stop Video'}
                active={isVideoMuted}
                activeColor="#ef4444"
                onClick={() => void toggleVideo()}
                testId="zoom-panel-btn-video"
                large={isExpanded}
              />
              {/* Virtual background — only for VB-eligible clinical credentials
                  (MEDICAL_DOCTOR / HOSPITAL_MANAGER). Opt-in: starts off, the
                  doctor toggles the branded BeHuman background on. */}
              {canUseVirtualBackground && (
                <PanelBtn
                  icon={<IconBackground size={isExpanded ? 18 : 15} />}
                  label="Background"
                  active={isVirtualBackgroundOn}
                  activeColor="#8b5cf6"
                  onClick={() => void toggleVirtualBackground()}
                  testId="zoom-panel-btn-vbg"
                  large={isExpanded}
                />
              )}
              <div className="relative">
                <PanelBtn
                  icon={<IconMessageCircle size={isExpanded ? 18 : 15} />}
                  label="Chat"
                  active={isChatOpen}
                  activeColor="#3b82f6"
                  onClick={() => setIsChatOpen((v) => !v)}
                  testId="zoom-panel-btn-chat"
                  large={isExpanded}
                />
                {chatUnread > 0 && (
                  <span
                    data-testid="zoom-panel-chat-unread"
                    className={`pointer-events-none absolute -top-1 -right-1 flex h-[16px] min-w-[16px] items-center justify-center rounded-full px-1 ${scribeText.micro} font-bold text-white`}
                    style={{ background: SCRIBE_ACCENT }}
                  >
                    {chatUnread}
                  </span>
                )}
              </div>
              {!isManagerUser && (
                <PanelBtn
                  icon={<IconScript size={isExpanded ? 18 : 15} />}
                  label={
                    scribeState === 'streaming'
                      ? 'Scribe On'
                      : scribeState === 'paused'
                        ? 'Paused'
                        : 'AI Scribe'
                  }
                  active={isScribeActive}
                  activeColor={scribeState === 'paused' ? '#f59e0b' : '#22c55e'}
                  disabled={scribeState === 'idle' && (isStarting || !canStartScribe)}
                  title={!canStartScribe ? 'Available to the treating physician' : undefined}
                  onClick={onScribeAction}
                  testId="zoom-panel-btn-scribe"
                  large={isExpanded}
                />
              )}
              <PanelBtn
                icon={<IconLink size={isExpanded ? 18 : 15} />}
                label={isSendingLink ? 'Sending…' : 'Send Link'}
                disabled={isSendingLink || !encounterId}
                onClick={() => void handleSendAppointmentLink()}
                testId="zoom-panel-btn-send-link"
                large={isExpanded}
              />
              {/* Fullscreen hosts Collapse + End here — the slim header only shows LIVE + timer. */}
              {isExpanded && (
                <>
                  <PanelBtn
                    icon={<IconArrowsMinimize size={18} />}
                    label="Collapse"
                    onClick={handleToggleExpand}
                    testId="zoom-panel-expand-toggle"
                    large
                  />
                  <button
                    type="button"
                    onClick={onEndClick}
                    data-testid="zoom-panel-end"
                    className={`ml-2 shrink-0 rounded-full border-0 bg-[#ef4444] px-6 py-[10px] ${scribeText.label} text-white hover:bg-[#dc2626]`}
                  >
                    End
                  </button>
                </>
              )}
            </div>
          </>
        )

        // §6 Visit Guidance, §9 Ask BeAssistant, and §5 scribe/transcript share
        // the column through draggable vertical dividers. Only OPEN sections
        // join the resizable group as <Panel>s — a collapsed section renders
        // statically so it keeps its intrinsic header height, and a divider
        // only appears when it has an open panel on each side to trade space
        // between (the library pairs it with the nearest panels, skipping
        // static children).
        const guidanceOpen = Boolean(patientId) && !guidanceCollapsed
        const scribeOpen = scribeState === 'idle' || !transcriptCollapsed
        const assistantOpen = Boolean(patientId) && !assistantCollapsed

        const guidanceSection = patientId ? (
          <VisitGuidanceSection
            patientId={patientId}
            collapsed={guidanceCollapsed}
            onToggleCollapsed={() => setGuidanceCollapsed((p) => !p)}
            rebuildKickedRef={guidanceRebuildKickedRef}
            hiddenTopics={hiddenTopics}
            onHideTopic={(reportKey, topicId) =>
              setHiddenTopics((prev) => ({
                reportKey,
                ids: new Set(prev?.reportKey === reportKey ? prev.ids : []).add(topicId),
              }))
            }
            onRestoreTopics={() => setHiddenTopics(null)}
          />
        ) : null

        const assistantSection = patientId ? (
          <AskAssistantSection
            patientId={patientId}
            collapsed={assistantCollapsed}
            onToggleCollapsed={() => setAssistantCollapsed((p) => !p)}
            threadPath={assistantThreadPath}
            onThreadPathChange={setAssistantThreadPath}
          />
        ) : null

        const scribeSection = (
          <AIScribeSection
            scribeState={scribeState}
            patientPresence={patientPresence}
            conversationStarted={transcriptWordCount > 0}
            isStarting={isStarting}
            onStart={() => canStartScribe && void start()}
            canStart={canStartScribe}
            collapsed={transcriptCollapsed}
            onToggleCollapsed={() => setTranscriptCollapsed((p) => !p)}
          >
            {/* §7 Live transcript */}
            <div
              data-testid="zoom-panel-transcript"
              className={
                'flex-1 overflow-y-auto px-[14px] pt-[6px] pb-[14px] ' +
                '[scrollbar-color:rgba(148,163,184,0.35)_transparent] [scrollbar-width:thin] ' +
                '[&::-webkit-scrollbar]:w-[6px] ' +
                '[&::-webkit-scrollbar-track]:bg-transparent ' +
                '[&::-webkit-scrollbar-thumb]:rounded-full ' +
                '[&::-webkit-scrollbar-thumb]:bg-slate-500/40 ' +
                '[&::-webkit-scrollbar-thumb]:transition-colors ' +
                'hover:[&::-webkit-scrollbar-thumb]:bg-slate-400/60'
              }
            >
              <TranscriptStream
                captions={captions}
                variant="dark"
                isStreaming={isStreaming && scribeState === 'streaming'}
                isComplete={scribeState === 'paused' && captions.length > 0}
              />
            </div>
          </AIScribeSection>
        )

        const transcriptColumn = isManagerUser ? (
          // Managers never see the scribe/transcript — keep an empty flex
          // filler so the expanded 2-column split stays layout-stable.
          <div className="min-h-0 flex-1" />
        ) : (
          <ResizableGroup orientation="vertical" className="min-h-0 flex-1">
            {/* §6b Doctor observations — AI reads these on End Session
                (`useFinalizeScribe`); hidden while the scribe is idle. Always
                intrinsic-height (autosized textarea), so never a panel. */}
            {scribeState !== 'idle' && (
              <DoctorObservations
                encounterId={encounterId ?? null}
                value={doctorObservations}
                onChange={setDoctorObservations}
                collapsed={observationsCollapsed}
                onToggleCollapsed={() => setObservationsCollapsed((p) => !p)}
              />
            )}
            {guidanceOpen ? (
              <Panel
                id="zoom-panel-guidance-pane"
                minSize="120px"
                className="flex min-h-0 flex-col"
              >
                {guidanceSection}
              </Panel>
            ) : (
              guidanceSection
            )}
            {guidanceOpen && (assistantOpen || scribeOpen) && (
              // `id` (not `data-testid`) — the v4 Separator renders its own
              // data-testid after spreading rest props, mirroring `id` into it.
              <Separator
                id="zoom-panel-section-divider"
                className="relative z-10 h-[6px] shrink-0 cursor-ns-resize bg-[#1e293b] transition-colors hover:bg-[var(--scribe-accent)] data-[resize-handle-active]:bg-[var(--scribe-accent)]"
              />
            )}
            {assistantOpen ? (
              // 160px keeps the header + input clear of the pane's
              // overflow-hidden edge at the smallest split.
              <Panel
                id="zoom-panel-assistant-pane"
                minSize="160px"
                className="flex min-h-0 flex-col"
              >
                {assistantSection}
              </Panel>
            ) : (
              assistantSection
            )}
            {assistantOpen && scribeOpen && (
              <Separator
                id="zoom-panel-assistant-divider"
                className="relative z-10 h-[6px] shrink-0 cursor-ns-resize bg-[#1e293b] transition-colors hover:bg-[var(--scribe-accent)] data-[resize-handle-active]:bg-[var(--scribe-accent)]"
              />
            )}
            {scribeOpen ? (
              <Panel id="zoom-panel-scribe-pane" minSize="120px" className="flex min-h-0 flex-col">
                {scribeSection}
              </Panel>
            ) : (
              scribeSection
            )}
          </ResizableGroup>
        )

        if (isExpanded) {
          // 2-column layout via `react-resizable-panels` (v4 API).
          // `Group` owns the drag math, pointer capture, and keyboard
          // support. Sizes are percentage strings; bounds keep the
          // split from collapsing either column. Persistence is left as
          // a follow-up (would wire `onLayoutChange` + localStorage).
          return (
            <ResizableGroup
              id={COLUMN_PANEL_GROUP_ID}
              orientation="horizontal"
              className="min-h-0 flex-1"
            >
              <Panel
                defaultSize={`${COLUMN_DEFAULT_LEFT_PCT}%`}
                minSize={`${COLUMN_MIN_LEFT_PCT}%`}
                maxSize={`${COLUMN_MAX_LEFT_PCT}%`}
                className="flex min-h-0 flex-col overflow-hidden"
                data-testid="zoom-panel-expanded-video-column"
              >
                {videoColumn}
              </Panel>
              <Separator
                className="relative z-10 w-[6px] shrink-0 cursor-ew-resize bg-[#1e293b] transition-colors hover:bg-[var(--scribe-accent)] data-[resize-handle-active]:bg-[var(--scribe-accent)]"
                data-testid="zoom-panel-column-divider"
              />
              <Panel
                className="flex min-h-0 flex-col overflow-hidden"
                data-testid="zoom-panel-expanded-transcript-column"
              >
                {transcriptColumn}
              </Panel>
            </ResizableGroup>
          )
        }

        // Collapsed: single column, original stacked layout.
        return (
          <>
            {videoColumn}
            {transcriptColumn}
          </>
        )
      })()}

      {/* §8 End-session prompt — absolute overlay inside the panel.
          FE-4 — `isGeneratingDraft || isAutoSubmitting` keeps the spinner
          state up through both the AI draft generation AND the auto-submit
          POST that follows, so the doctor never sees the edit form between
          "Generating Progress Note…" and the read-only viewer they land on
          post-navigate. */}
      {endPromptOpen && (
        <EndSessionPrompt
          isFinalizing={isFinalizing}
          isGeneratingDraft={isGeneratingDraft || isAutoSubmitting || isProcessingNote}
          draftProgress={draftProgress}
          draftReady={draftReady}
          closing={promptClosing}
          draftError={draftRetryAvailable}
          onConfirmEnd={() => void onCreateProgressNote()}
          onCancel={() => setEndPromptOpen(false)}
          onRetryDraft={() => void onRetryDraft()}
          onWriteManually={() => void onWriteManually()}
        />
      )}

      {tooShortOpen && (
        <ConversationTooShortModal
          empty={transcriptWordCount === 0}
          onKeepRecording={onKeepRecording}
          onGenerateAnyway={onGenerateAnyway}
          onDiscard={onDiscardTooShort}
        />
      )}

      {/* In-call chat drawer. Reads `chatMessages` + `sendChatMessage` from
          `useCallSession()` — the underlying Zoom listener is wired in
          `CallSessionProvider.joinSession`, so messages keep flowing into
          state even when the drawer is closed. */}
      <ChatDrawer opened={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </aside>
  )
}
